export type FpCookieData = {
    invitationCode: string;
    lastIndex?: number;
    updatedAt: number;
};

const COOKIE_NAME = "fp_invitation";
const DEFAULT_DAYS = 30;

function safeJsonParse<T>(raw: string): T | null {
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

function setCookie(name: string, value: string, days = DEFAULT_DAYS) {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000)
        .toUTCString();
    const secure = typeof window !== "undefined" &&
        window.location.protocol === "https:";

    document.cookie =
        `${name}=${
            encodeURIComponent(value)
        }; Expires=${expires}; Path=/; SameSite=Lax` +
        (secure ? "; Secure" : "");
}

function getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;

    const needle = `${name}=`;
    const parts = document.cookie.split(";").map((p) => p.trim());
    const found = parts.find((p) => p.startsWith(needle));
    if (!found) return null;

    return decodeURIComponent(found.slice(needle.length));
}

function deleteCookie(name: string) {
    document.cookie =
        `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
}

export function readFpCookie(): FpCookieData | null {
    const raw = getCookie(COOKIE_NAME);
    if (!raw) return null;

    const parsed = safeJsonParse<FpCookieData>(raw);
    if (!parsed?.invitationCode) return null;

    return parsed;
}

export function writeFpCookie(data: FpCookieData) {
    setCookie(COOKIE_NAME, JSON.stringify(data));
}

export function upsertFpCookie(
    partial: Partial<FpCookieData> & { invitationCode?: string },
) {
    const prev = readFpCookie();
    const invitationCode =
        (partial.invitationCode ?? prev?.invitationCode ?? "").trim();

    if (!invitationCode) {
        clearFpCookie();
        return;
    }

    const resolvedLastIndex = typeof partial.lastIndex === "number"
        ? partial.lastIndex
        : typeof prev?.lastIndex === "number"
        ? prev.lastIndex
        : undefined;

    const next: FpCookieData = {
        invitationCode,
        updatedAt: Date.now(),
        ...(typeof resolvedLastIndex === "number"
            ? { lastIndex: resolvedLastIndex }
            : {}),
    };

    writeFpCookie(next);
}

export function clearFpCookie() {
    deleteCookie(COOKIE_NAME);
}
